import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AdminAttentionService, AttentionCounts } from '@cnt-admin/data-access';
import { IonContent, IonMenuToggle } from '@ionic/angular/standalone';
import { filter } from 'rxjs';

interface INavLink {
  label: string;
  /** Material Symbols ligature name. */
  icon: string;
  link: string[];
  /** Match the URL exactly, so a parent section isn't lit up on its children. */
  exact?: boolean;
  /** Which "Needs attention" count to show as a badge (hidden at 0 or unknown). */
  count?: BadgeCount;
}

type BadgeCount = keyof Pick<
  AttentionCounts,
  'requests' | 'overdueRequests' | 'pois' | 'caretakers' | 'payouts' | 'affiliates'
>;

interface INavGroup {
  /** Empty for a group shown without a heading (Home). */
  label: string;
  links: INavLink[];
  /** Open until the admin folds it; other headed groups start folded. A group
   *  with no heading (Home) never folds. */
  openByDefault?: boolean;
}

/** localStorage key for the folding groups the admin has opened or closed. */
const OPEN_GROUPS_KEY = 'admin-nav-open-groups';

/**
 * Contents of the admin side menu, in cnt-landing's left-rail style (the
 * account page's nav): a floating white card on the cream page with the logo
 * on top, grouped links with Material Symbols icons, and the current section
 * in trinidad. The `ion-menu` itself sits in app.component.html, directly inside
 * the split pane, so it can stay pinned open on wide screens.
 */
@Component({
  selector: 'admin-header',
  standalone: true,
  imports: [IonContent, IonMenuToggle, RouterLink, RouterLinkActive],
  template: `
    <ion-content class="rail">
      <div class="rail-card">
        <a class="brand" routerLink="/" aria-label="CurbNTurf Admin — home">
          <img src="/images/CNT_Logo_V_Orange.svg" alt="" width="34" height="44" />
          <span>
            <strong>CurbNTurf</strong>
            <small>Admin</small>
          </span>
        </a>
        <nav aria-label="Admin sections">
          @for (group of groups; track group.label) {
            @if (group.label) {
              <button
                type="button"
                class="group-label group-toggle"
                [class.folded]="!isOpen(group)"
                [attr.aria-expanded]="isOpen(group)"
                (click)="toggle(group)"
              >
                <span class="group-name">{{ group.label }}</span>
                <!-- A folded group still says something is waiting in it. -->
                @if (!isOpen(group) && groupTotal(group); as total) {
                  <span class="badge" [attr.aria-label]="total + ' waiting'">{{ total }}</span>
                }
                <span class="material-symbols-outlined chevron" aria-hidden="true">{{
                  isOpen(group) ? 'expand_less' : 'expand_more'
                }}</span>
              </button>
            }
            @for (item of !group.label || isOpen(group) ? group.links : []; track item.label) {
              <ion-menu-toggle [autoHide]="false">
                <a
                  class="nav-item"
                  [routerLink]="item.link"
                  routerLinkActive="active"
                  [routerLinkActiveOptions]="{ exact: !!item.exact }"
                  ariaCurrentWhenActive="page"
                >
                  <span class="material-symbols-outlined" aria-hidden="true">{{ item.icon }}</span>
                  <span class="nav-label">{{ item.label }}</span>
                  @if (item.count && badges[item.count]; as n) {
                    <span
                      class="badge"
                      [attr.aria-label]="n + (item.count === 'overdueRequests' ? ' overdue' : ' waiting')"
                      >{{ n }}</span
                    >
                  }
                </a>
              </ion-menu-toggle>
            }
          }
        </nav>
      </div>
    </ion-content>
  `,
  styles: [
    `
      /* Fill the ion-menu so the rail's ion-content gets a height to scroll in. */
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
      }
      .rail {
        --background: transparent;
        --padding-start: 12px;
        --padding-end: 0;
        --padding-top: 12px;
        --padding-bottom: 12px;
      }
      .rail-card {
        min-height: 100%;
        padding: 20px 12px 24px;
        border: 1px solid var(--cnt-line);
        border-radius: 1rem;
        background: #fff;
        box-shadow: var(--cnt-card-shadow);
      }
      .brand {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 0 8px 20px;
        margin-bottom: 8px;
        border-bottom: 1px solid var(--cnt-line);
        color: var(--cnt-ink);
      }
      .brand span {
        display: flex;
        flex-direction: column;
        line-height: 1.1;
      }
      .brand strong {
        font-size: 1.125rem;
        letter-spacing: -0.01em;
      }
      .brand small {
        margin-top: 2px;
        color: var(--cnt-trinidad);
        font-size: 0.65rem;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
      }
      .group-label {
        margin: 18px 12px 6px;
        color: var(--cnt-muted);
        font-size: 0.65rem;
        font-weight: 700;
        line-height: 1;
        letter-spacing: 0.12em;
        text-transform: uppercase;
      }
      .group-toggle {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: calc(100% - 24px);
        padding: 0;
        border: 0;
        background: none;
        cursor: pointer;
        font-family: inherit;
        text-align: left;
      }
      .group-name {
        flex: 1 1 auto;
      }
      .group-toggle .badge {
        margin-right: 6px;
        letter-spacing: 0;
      }
      /* Folded headings sit closer together, so they read as one block. */
      .group-toggle.folded {
        margin-top: 10px;
        margin-bottom: 10px;
      }
      .group-toggle:hover {
        color: var(--cnt-ink);
      }
      .group-toggle:focus-visible {
        outline: 2px solid var(--cnt-green);
        outline-offset: 4px;
        border-radius: 4px;
      }
      .chevron {
        font-size: 16px;
      }
      .nav-item {
        display: flex;
        align-items: center;
        gap: 12px;
        margin: 2px 0;
        padding: 9px 12px;
        border-radius: 0.75rem;
        color: var(--cnt-ink);
        font-size: 0.875rem;
        font-weight: 700;
        transition:
          background-color 0.15s ease,
          color 0.15s ease;
      }
      .nav-item .material-symbols-outlined {
        flex: none;
        font-size: 20px;
        color: var(--cnt-muted);
      }
      .nav-item:hover {
        background: rgba(247, 245, 236, 0.8);
      }
      .nav-item:focus-visible {
        outline: 2px solid var(--cnt-green);
        outline-offset: 1px;
      }
      .nav-label {
        flex: 1 1 auto;
      }
      .badge {
        flex: none;
        min-width: 22px;
        padding: 2px 7px;
        border-radius: 9999px;
        background: var(--cnt-trinidad);
        color: #fff;
        font-size: 0.7rem;
        font-weight: 700;
        line-height: 1.4;
        text-align: center;
      }
      .nav-item.active {
        background: rgba(var(--cnt-trinidad-rgb), 0.1);
        color: var(--cnt-trinidad);
      }
      .nav-item.active .material-symbols-outlined {
        color: var(--cnt-trinidad);
        font-variation-settings:
          'FILL' 1,
          'wght' 400;
      }
    `,
  ],
})
export class AdminHeaderComponent implements OnInit {
  private readonly attention = inject(AdminAttentionService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);

  /** Badge text per count, e.g. "3" or "99+"; empty when 0 or not loaded. */
  badges: Partial<Record<BadgeCount, string>> = {};
  /** The same counts as numbers, for a folded group's total. */
  private counts: Partial<Record<BadgeCount, number>> = {};

  /** Groups the admin opened (true) or folded (false); kept between visits. */
  private readonly opened: Record<string, boolean> = readOpenGroups();
  /** Groups unfolded only because a page in them was opened; not saved, so the admin's choice stands. */
  private readonly shown = new Set<string>();

  isOpen(group: INavGroup): boolean {
    return this.shown.has(group.label) || (this.opened[group.label] ?? !!group.openByDefault);
  }

  toggle(group: INavGroup): void {
    const open = !this.isOpen(group);
    this.shown.delete(group.label);
    this.setOpen(group, open);
  }

  /** What's waiting across a group's pages, e.g. "17" or "99+"; empty at 0. */
  groupTotal(group: INavGroup): string {
    const n = group.links.reduce((sum, l) => sum + (l.count ? this.counts[l.count] ?? 0 : 0), 0);
    return n ? (n > 99 ? '99+' : String(n)) : '';
  }

  private setOpen(group: INavGroup, open: boolean): void {
    this.opened[group.label] = open;
    try {
      localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(this.opened));
    } catch {
      // Storage blocked: the choice just lasts until the page reloads.
    }
  }

  /** Unfolds the group holding this page, so the page's own link is always in view.
   *  The longest matching link wins: /sites/boondock is Boondocking, not /sites. */
  private openGroupOf(url: string): void {
    const path = url.split(/[?#]/)[0];
    let best: { group: INavGroup; length: number } | undefined;
    for (const group of this.groups) {
      if (!group.label) continue;
      for (const l of group.links) {
        const base = `/${l.link.filter(Boolean).join('/')}`;
        if ((path === base || path.startsWith(`${base}/`)) && base.length > (best?.length ?? 0)) {
          best = { group, length: base.length };
        }
      }
    }
    if (best && !this.isOpen(best.group)) this.shown.add(best.group.label);
  }

  ngOnInit(): void {
    this.attention.counts$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((c) => {
      const label = (n: number | null) => (n ? (n > 99 ? '99+' : String(n)) : '');
      this.counts = {
        requests: c.requests ?? 0,
        overdueRequests: c.overdueRequests ?? 0,
        pois: c.pois ?? 0,
        caretakers: c.caretakers ?? 0,
        payouts: c.payouts ?? 0,
        affiliates: c.affiliates ?? 0,
      };
      this.badges = {
        requests: label(c.requests),
        overdueRequests: label(c.overdueRequests),
        pois: label(c.pois),
        caretakers: label(c.caretakers),
        payouts: label(c.payouts),
        affiliates: label(c.affiliates),
      };
    });
    // The menu outlives every page: check the badges after each navigation
    // (a no-op while they're under five minutes old).
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => {
        this.openGroupOf((e as NavigationEnd).urlAfterRedirects);
        void this.attention.refreshIfStale();
      });
    this.openGroupOf(this.router.url);
    void this.attention.refreshIfStale();
  }

  /** Grouped by what the admin is doing. Labels match the pages' titles. */
  readonly groups: INavGroup[] = [
    {
      label: '',
      links: [{ label: 'Home', icon: 'home', link: [''], exact: true }],
    },
    {
      label: 'Customer service',
      openByDefault: true,
      links: [
        { label: 'Tickets', icon: 'inbox', link: ['', 'support', 'tickets'] },
        { label: 'Customers', icon: 'support_agent', link: ['', 'support', 'customers'] },
        {
          label: 'Booking watchlist',
          icon: 'assignment_late',
          link: ['', 'support', 'bookings'],
          count: 'overdueRequests',
        },
        { label: 'Users', icon: 'person', link: ['', 'users'] },
      ],
    },
    {
      label: 'Bookings & money',
      openByDefault: true,
      links: [
        { label: 'Reservations', icon: 'confirmation_number', link: ['', 'reservations'], count: 'requests' },
        { label: 'Payouts', icon: 'account_balance_wallet', link: ['', 'payouts'], count: 'payouts' },
        { label: 'Affiliates', icon: 'group', link: ['', 'affiliates'], count: 'affiliates' },
      ],
    },
    {
      label: 'Listings',
      links: [
        { label: 'Private listings', icon: 'cottage', link: ['', 'sites'], exact: true },
        { label: 'Boondocking', icon: 'camping', link: ['', 'sites', 'boondock'] },
        { label: 'Listing quality', icon: 'fact_check', link: ['', 'quality'] },
        { label: 'Reviews', icon: 'reviews', link: ['', 'reviews'] },
        { label: 'POIs', icon: 'location_on', link: ['', 'pois'], count: 'pois' },
        { label: 'Caretaker submissions', icon: 'key', link: ['', 'rating', 'caretaker'], count: 'caretakers' },
      ],
    },
    {
      label: 'Grow hosts',
      links: [
        { label: 'Help them finish', icon: 'construction', link: ['', 'unfinished'] },
        { label: 'Hosts to nudge', icon: 'campaign', link: ['', 'nudge'] },
      ],
    },
    {
      label: 'Content',
      links: [
        { label: 'Article Builder', icon: 'edit_note', link: ['', 'article-builder'] },
        { label: 'Email templates', icon: 'mail', link: ['', 'email-template'] },
      ],
    },
    {
      label: 'Reports',
      links: [
        { label: 'Analytics', icon: 'monitoring', link: ['', 'analytics'] },
        { label: 'Weekly report', icon: 'bar_chart', link: ['', 'reports'] },
      ],
    },
    {
      label: 'System',
      links: [
        { label: 'Search tools', icon: 'manage_search', link: ['', 'search'] },
        { label: 'Logs', icon: 'dns', link: ['', 'log'] },
      ],
    },
  ];
}

function readOpenGroups(): Record<string, boolean> {
  try {
    const raw = JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY) ?? '{}');
    return raw && typeof raw === 'object' ? raw : {};
  } catch {
    return {};
  }
}
